/* FOM · publicacao — a fronteira entre o que a casa sabe e o que o mundo ve.

   Tres garantias que esta tela NAO implementa, e por isso pode ser honesta
   sobre elas:

   · a camada PRIVATE nao atravessa. O scrub e do banco (trigger C-08). Se
     esta tela filtrasse, existiria uma segunda politica de privacidade — e
     duas politicas de privacidade sao zero politicas de privacidade;
   · a publicacao e IMUTAVEL. Nao ha editar, nao ha apagar. Publicar de novo
     cria uma versao nova;
   · so `owner` publica. `editor` recebe 403 do servidor.

   O que a tela faz e mostrar a prova: cada versao carrega o sha256 do seu
   snapshot, e a BASE continua verificavel por fingerprint proprio. */
import type { Api } from '../core/api.ts';
import { ApiError } from '../core/api.ts';
import type { MemberRole, PublicationResumo } from '../core/types.ts';
import { el, botao, campo, area, limpar, vazio, dataCurta } from './dom.ts';

export type PublicacaoOpcoes = {
  api: Api;
  org: string;
  mapa: string;
  papel: MemberRole;
  aoErro: (erro: unknown) => void;
};

export function montarPublicacao(host: HTMLElement, o: PublicacaoOpcoes) {
  let versoes: PublicationResumo[] = [];
  let prova: { fingerprint_base_only: string; nodes_walked: number } | null = null;
  let publicando = false;

  async function carregar(): Promise<void> {
    versoes = await o.api.publicacoes(o.org, o.mapa);
    prova = await o.api.fingerprintBaseOnly(o.org, o.mapa);
  }

  function render(): void {
    limpar(host);

    const cab = el('div', 'gaveta-cabecalho');
    cab.appendChild(el('h3', 'gaveta-titulo', 'Publicacao'));
    cab.appendChild(el('span', 'selo selo-publico', 'PUBLICO'));
    host.appendChild(cab);

    host.appendChild(el('p', 'gaveta-nota',
      'Publicar congela uma versao do mapa. A camada privada nao atravessa — '
      + 'quem remove e o banco, nao esta tela. Publicacao nao se edita nem se apaga.'));

    /* --- o vivo e as fotografias --- */
    const fluxo = el('div', 'pub-fluxo');
    fluxo.setAttribute('role', 'img');
    fluxo.setAttribute('aria-label', 'O mapa vivo gera fotografias imutaveis ao publicar');
    fluxo.appendChild(el('span', 'pub-fluxo-vivo', 'MAPA VIVO'));
    for (const v of [...versoes].sort((a, b) => a.version - b.version)) {
      fluxo.appendChild(el('span', 'pub-fluxo-seta', '→'));
      const b = el('b', '', 'v' + String(v.version));
      b.title = 'fotografia imutavel · ' + dataCurta(v.published_at);
      fluxo.appendChild(b);
    }
    if (!versoes.length) {
      fluxo.appendChild(el('span', 'pub-fluxo-seta', '→'));
      fluxo.appendChild(el('span', '', 'nenhuma fotografia ainda'));
    }
    host.appendChild(fluxo);

    /* --- prova permanente da BASE --- */
    if (prova) {
      const p = el('div', 'prova');
      p.appendChild(el('span', 'campo-rotulo', 'Impressao digital da BASE'));
      p.appendChild(el('code', 'prova-hash', prova.fingerprint_base_only));
      p.appendChild(el('span', 'campo-dica',
        String(prova.nodes_walked) + ' nos percorridos · recalculada do payload original, '
        + 'nunca copiada'));
      host.appendChild(p);
    }

    /* --- publicar --- */
    if (o.papel === 'owner') host.appendChild(formPublicar());
    else {
      host.appendChild(el('p', 'gaveta-aviso',
        'Seu papel e ' + o.papel + '. Somente `owner` publica.'));
    }

    /* --- versoes --- */
    host.appendChild(el('h4', 'gaveta-secao', 'Versoes publicadas · ' + String(versoes.length)));
    if (!versoes.length) {
      host.appendChild(vazio(
        'Nada publicado ainda.',
        'Enquanto nao houver publicacao, nada deste mapa saiu para fora.',
      ));
      return;
    }
    const ul = el('ul', 'lista-pub');
    for (const v of versoes) {
      const li = el('li', 'pub-item');
      li.appendChild(el('span', 'pub-versao', 'v' + String(v.version)));
      li.appendChild(el('span', 'pub-data', dataCurta(v.published_at)));
      const hash = el('code', 'pub-hash', v.snapshot_sha256.slice(0, 16) + '…');
      hash.title = v.snapshot_sha256;
      li.appendChild(hash);
      ul.appendChild(li);
    }
    host.appendChild(ul);
  }

  function formPublicar(): HTMLElement {
    const form = el('form', 'formulario');
    form.noValidate = true;
    const nota = area('', 2, 'o que mudou nesta versao');
    form.appendChild(campo('Nota da versao', nota, 'opcional, mas e o que explica a versao depois'));

    const acoes = el('div', 'formulario-acoes');
    const enviar = el('button', 'botao botao-principal',
      versoes.length ? 'Publicar v' + String(versoes[0]!.version + 1) : 'Publicar v1');
    enviar.type = 'submit';
    enviar.disabled = publicando;
    acoes.appendChild(enviar);
    form.appendChild(acoes);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      if (publicando) return;
      publicando = true;
      enviar.disabled = true;
      void (async () => {
        try {
          await o.api.publicar(o.org, o.mapa,
            nota.value.trim() ? { note: nota.value.trim() } : {});
          await carregar();
        } catch (err) {
          o.aoErro(err);
        } finally {
          publicando = false;
          render();
        }
      })();
    });
    return form;
  }

  return {
    async mostrar(): Promise<void> {
      try {
        await carregar();
        render();
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          limpar(host);
          host.appendChild(el('h3', 'gaveta-titulo', 'Publicacao'));
          host.appendChild(vazio('Sem acesso.', 'Seu papel nao le publicacoes.'));
          return;
        }
        o.aoErro(err);
      }
    },
  };
}
